import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  CircularProgress,
  Typography
} from '@material-ui/core'
import Select from 'react-select'
import { graphql } from 'react-apollo'
import gql from 'graphql-tag'
import withSharedSnackbar from '../../HOCs/withSharedSnackbar'

// GraphQL Queries and Mutations
const GET_MODEL = gql`
  query GetModel($id: Int!, $clientLoginId: Int!) {
    getModel(id: $id, clientLoginId: $clientLoginId) {
      status
      message
      data {
        id
        name
        cum_weight
        engine_hours
        fe_variable
        brand {
          id
          name
        }
      }
    }
  }
`

const UPDATE_MODEL = gql`
 mutation UpdateModel(
  $id: Int!
  $clientLoginId: Int!
  $name: String
  $truckBrandId: Int
  $engineHours: Float
  $feVariable: Float
) {
  updateModel(
    id: $id
    clientLoginId: $clientLoginId
    name: $name
    truckBrandId: $truckBrandId
    engineHours: $engineHours
    feVariable: $feVariable
  ) {
    status
    message
  }
}

`

const GET_BRANDS = gql`
  query GetBrands($clientLoginId: Int!) {
    getBrands(clientLoginId: $clientLoginId) {
      status
      message
      data {
        id
        name
      }
    }
  }
`

const EditModelDialog = ({
  open,
  onClose,
  modelId,
  clientLoginId,
  getModel,
  updateModel,
  getBrands,
  onEditSuccess,
  openSnackbar
}) => {
  const [formData, setFormData] = useState({
    name: '',
    truckBrandId: null,
    truckBrandName: '',
    engineHours: '',
    feVariable: ''
  })
  const [error, setError] = useState({})
  const [brands, setBrands] = useState([])
  const [loading, setLoading] = useState(false)

  const { refetch: refetchBrands } = getBrands
  const { refetch: refetchModel } = getModel

  useEffect(() => {
    if (open && modelId) {
      fetchModel()
      fetchBrands()
    }
  }, [open, modelId])

  const fetchModel = async () => {
    setLoading(true)
    try {
      const response = await refetchModel({
        id: modelId,
        clientLoginId: parseInt(clientLoginId)
      })
      const model = response.data.getModel.data[0]
      if (model) {
        setFormData({
          name: model.name,
          truckBrandId: model.brand.id,
          truckBrandName: model.brand.name.toLowerCase(),
          engineHours: model.engine_hours || '',
          feVariable: model.fe_variable || ''
        })
      }
    } catch (error) {
      console.error('Failed to fetch model:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchBrands = async () => {
    try {
      const response = await refetchBrands()
      setBrands(response.data.getBrands.data || [])
    } catch (error) {
      console.error('Failed to fetch brands:', error)
    }
  }

  const handleInputChange = field => event => {
    setFormData({ ...formData, [field]: event.target.value })
  }

  const validateForm = () => {
    const validationErrors = {}
    if (!formData.name.trim()) validationErrors.name = 'Model name is required.'
    if (!formData.truckBrandId) validationErrors.truckBrandId = 'Please select a truck brand.'
    if (formData.truckBrandName === 'volvo') {
      const numericOnly = (formData.engineHours || '').replace(/[^0-9]/g, '')
      if (isNaN(parseFloat(formData.engineHours))) {
        validationErrors.engineHours = 'Engine hours must be a valid number.'
      } else if (numericOnly.length > 5) {
        validationErrors.engineHours = 'Engine hours must not exceed 5 digits.'
      }
    }
    if (formData.feVariable && isNaN(parseFloat(formData.feVariable))) {
      validationErrors.feVariable = 'FE Variable must be a valid number.'
    } else if (formData.feVariable && formData.feVariable.replace(/[^0-9]/g, '').length > 3) {
      validationErrors.feVariable = 'FE Variable must not exceed 3 digits.'
    }
    setError(validationErrors)
    return Object.keys(validationErrors).length === 0
  }

  const handleSave = () => {
    if (!validateForm()) return

    updateModel({
      variables: {
        id: modelId,
        clientLoginId: parseInt(clientLoginId),
        name: formData.name,
        truckBrandId: parseInt(formData.truckBrandId),
        engineHours: formData.truckBrandName === 'volvo' ? parseFloat(formData.engineHours) : null,
        feVariable: formData.feVariable ? parseFloat(formData.feVariable) : null
      }
    })
      .then(({ data }) => {
        if (data.updateModel.status) {
          openSnackbar(`${data.updateModel.message}`, { variant: 'success' })
          onClose()
          onEditSuccess()
        } else {
          openSnackbar(`${data.updateModel.message}`, { variant: 'error' })
        }
      })
      .catch(error => {
        console.error('Failed to update model:', error)
        openSnackbar('Failed to update model', { variant: 'error' })
      })
  }

  const brandOptions = brands.map(brand => ({
    value: brand.id,
    label: brand.name
  }))

  const handleSelectChange = selectedOption => {
    setError(prev => ({ ...prev, truckBrandId: '' }))
    setFormData({
      ...formData,
      truckBrandId: selectedOption ? selectedOption.value : null,
      truckBrandName: selectedOption ? selectedOption.label.toLowerCase() : ''
    })
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>Edit Model</DialogTitle>
      <DialogContent>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
            <CircularProgress />
          </div>
        ) : (
          <div style={{ padding: '10px' }}>
            <TextField
              label="Model Name"
              fullWidth
              margin="normal"
              value={formData.name}
              onChange={handleInputChange('name')}
              error={!!error.name}
              helperText={error.name}
              variant="outlined"
              InputProps={{
                style: {
                  borderRadius: 8,
                  backgroundColor: '#fafafa',
                  padding: '10px'
                }
              }}
              InputLabelProps={{
                style: {
                  color: 'gray',
                  fontWeight: 'bold'
                }
              }}
              FormHelperTextProps={{
                style: {
                  color: '#f44336'
                }
              }}
            />

            <div style={{ marginTop: '16px' }}>
              <Typography variant="subtitle1" gutterBottom>
                Truck Brand
              </Typography>
              <Select
                options={brandOptions}
                value={brandOptions.find(option => option.value === formData.truckBrandId) || null}
                onChange={handleSelectChange}
                placeholder="Select Truck Brand"
                styles={{ container: base => ({ ...base, marginBottom: 16 }) }}
              />
              {error.truckBrandId && (
                <Typography color="error" variant="body2" style={{ marginBottom: 16 }}>
                  {error.truckBrandId}
                </Typography>
              )}
            </div>

            {formData.truckBrandName === 'volvo' && (
              <div>
                <TextField
                  label="Engine Hours"
                  fullWidth
                  variant="outlined"
                  value={formData.engineHours}
                  onChange={e => {
                    const value = e.target.value
                    const numericOnly = value.replace(/[^0-9]/g, '')
                    const isValidFormat = /^\d*\.?\d*$/.test(value)

                    if (isValidFormat && numericOnly.length <= 5) {
                      setFormData(prev => ({ ...prev, engineHours: value }))
                      setError(prev => ({ ...prev, engineHours: '' }))
                    }
                  }}
                  error={!!error.engineHours}
                  helperText={error.engineHours}
                  style={{ marginBottom: '16px' }}
                />

                <TextField
                  label="FE Variable"
                  fullWidth
                  variant="outlined"
                  value={formData.feVariable}
                  onChange={(e) => {
                    const value = e.target.value
                    const numericOnly = value.replace(/[^0-9]/g, '')
                    const isValidFormat = /^\d*\.?\d*$/.test(value)
                    if (isValidFormat && numericOnly.length <= 3) {
                      setFormData(prev => ({ ...prev, feVariable: value }))
                      setError(prev => ({ ...prev, feVariable: '' }))
                    }
                  }}
                  error={!!error.feVariable}
                  helperText={error.feVariable}
                  style={{ marginBottom: '16px' }}
                />

              </div>
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions style={{ paddingRight: '20px', marginBottom: '10px' }}>
        <Button
          onClick={onClose}
          variant="outlined"
          style={{ borderColor: '#f44336', color: '#f44336', borderRadius: 8 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          style={{
            backgroundColor: '#66bb6a',
            color: '#ffffff',
            borderRadius: 8
          }}
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default graphql(GET_MODEL, {
  name: 'getModel',
  options: props => ({
    variables: { id: props.modelId, clientLoginId: props.clientLoginId },
    skip: !props.modelId
  })
})(
  graphql(GET_BRANDS, {
    name: 'getBrands',
    options: props => ({ variables: { clientLoginId: props.clientLoginId } })
  })(
    graphql(UPDATE_MODEL, {
      name: 'updateModel'
    })(withSharedSnackbar(EditModelDialog))
  )
)



// WEBPACK FOOTER //
// ./src/components/Pages/modalConfigure/EditModelModal.jsx